// Phase-A R1.1 — AppShell: 3-mode chrome composer.
// PUBLIC: Nav (own drawer) + content + Footer. AUTH_FLOW: minimal Nav.
// WORKSPACE: Nav (workspace mode) + sidebar (fixed lg+, Sheet drawer <lg)
// + content. Replaces the inline Layout that lived in router.tsx.
// R1.2 will inject Breadcrumbs into the slot below the topbar.

import React from "react";
import { Outlet } from "react-router-dom";

import { ErrorBoundary } from "../auth/ErrorBoundary";
import { useAuth } from "../auth/AuthContext";
import { ROLES, useRole } from "../role";
import { AuthLoadingSkeleton } from "../components/AuthLoadingSkeleton";
import { RoleDenied } from "../components/RoleDenied";
import { RetiredRoute } from "../components/RetiredRoute";
import { BottomNav, cn, Sheet, SheetContent } from "../ui";
import { ScrollProgress } from "../motion";
import { Nav, Footer } from "../shared";
import { RoleSideNav } from "../sidenav";
import { MiniRail } from "./MiniRail";
import { useGo, useCurrentRoute } from "../router";
import { getRouteKind, type RouteKind } from "../router/route-classification";
import { isRouteAllowedForRole } from "../router/route-roles";
import { isRetiredRoute } from "../config/adapt-flags";
import { useMediaQuery } from "../hooks/useMediaQuery";
import { Breadcrumbs } from "./Breadcrumbs";
import { OrgAttribution } from "../components/OrgAttribution";

type NavMode = "public" | "workspace" | "auth_flow";

const navModeFor = (kind: RouteKind): NavMode =>
  kind === "WORKSPACE" ? "workspace" : kind === "AUTH_FLOW" ? "auth_flow" : "public";

// R7.12 — replaces the binary "open"|"closed" with a width-mode discriminator.
// The migration shim in readSidebarPref maps legacy values:
//   "open"   → { mode: "expanded" }
//   "closed" → { mode: "mini" }
// so users with a previous preference keep something close to their
// expectation. Default for new visitors: mini.
type SidebarMode = "mini" | "expanded";
interface SidebarPref {
  mode: SidebarMode;
}

const readSidebarPref = (): SidebarPref => {
  if (typeof window === "undefined") return { mode: "mini" };
  try {
    const raw = localStorage.getItem("digiu_sidebar_pref");
    if (!raw) return { mode: "mini" };
    if (raw === "open") return { mode: "expanded" };       // legacy → expanded
    if (raw === "closed") return { mode: "mini" };         // legacy → mini
    const parsed = JSON.parse(raw);
    return parsed?.mode === "expanded" ? { mode: "expanded" } : { mode: "mini" };
  } catch {
    return { mode: "mini" };
  }
};

const writeSidebarPref = (pref: SidebarPref): void => {
  try { localStorage.setItem("digiu_sidebar_pref", JSON.stringify(pref)); } catch {}
};

export const AppShell: React.FC = () => {
  const { id: route, param: routeParam } = useCurrentRoute();
  const go = useGo();
  const auth = useAuth();
  const { activeRoleId } = useRole();
  // R-MARKET P4 — pass the sub-path so the SPLIT "market" segment classifies
  // correctly: bare /market + /market/course/* are PUBLIC, /market/studio* and
  // /market/review* stay WORKSPACE (auth-gated). Every other route ignores the arg.
  const kind: RouteKind = getRouteKind(route, routeParam ?? undefined);
  const isWorkspace = kind === "WORKSPACE";

  // R-MARKET fix — the «بازار دانش» catalog (/market) + course detail
  // (/market/course/:id) are PUBLIC so a guest can browse without a login. But an
  // AUTHENTICATED visitor should keep their workspace chrome (rail/sidebar/breadcrumbs/
  // bottom-nav) instead of dropping to the bare public layout — otherwise the sidebar
  // "disappears" when they open the marketplace. `chromeWorkspace` drives the VISUAL
  // chrome only; the auth gate + role gate below stay on the real `isWorkspace` so
  // guests are never forced to log in and the author-only "market" role-key never
  // denies a student browsing the public catalog.
  const isPublicMarket = route === "market" && kind === "PUBLIC";
  const chromeWorkspace = isWorkspace || (isPublicMarket && auth.isAuthenticated);

  // R-NAV-1/2 (spec §2.6) — role gate. A wrong-role visit to a workspace
  // route shows a denial INSIDE the shell (navbar + sidebar stay, so the
  // user can navigate away) rather than redirecting to /login. Only
  // meaningful once authenticated; the auth gate below handles no-session.
  // pathKey = first two segments ("admin/departments") so the gate can be
  // path-aware (faculty_admin reaches scoped /admin/* pages, not all of them).
  const pathKey = routeParam ? `${route}/${routeParam.split("/")[0]}` : route;
  const roleDenied =
    isWorkspace &&
    auth.isAuthenticated &&
    !isRouteAllowedForRole(route, pathKey, activeRoleId);

  // S-NRI-5 سطل الف — گیتِ مسیرِ بازنشسته. تا پیش از این، بازنشستگی فقط نامِ مسیر
  // را از ناوبری/چیپ/⌘K حذف می‌کرد و URL مستقیم همان صفحهٔ دانشگاهیِ مدرک‌ده را
  // کامل رندر می‌کرد. AppShell تنها گلوگاهِ رندرِ همهٔ مسیرهاست، پس گیت اینجاست.
  // برخلافِ roleDenied این گیت به kind و به احراز هویت وابسته نیست: «apply» و
  // «admissions» عمومی‌اند (PUBLIC) و باید برای مهمان هم بازنشسته باشند.
  // pathKey اول بررسی می‌شود تا «tuition/loans» پیش از «tuition» بگیرد.
  const retired = isRetiredRoute(pathKey) || isRetiredRoute(route);

  // R1.3 B5 — privacy: an authenticated visitor hitting the landing
  // (/ or /home) must NOT see any chrome rendered with their identity
  // before the redirect takes effect. Gate the shell behind a skeleton.
  const isLandingRoute = route === "" || route === "home";
  const redirectAuthedFromLanding = isLandingRoute && auth.isAuthenticated;

  // R7.12 — sidebar is now viewport-conditional:
  //   ≥1024px (workspace): persistent rail inline in the workspace
  //     grid. Width animates between 72px (mini) and 280px (expanded).
  //     The rail is ALWAYS in the DOM at this breakpoint; the mode
  //     persists across reloads via localStorage `digiu_sidebar_pref`.
  //   <1024px (workspace): R6.6 Sheet drawer unchanged — overlay,
  //     lazy-mounted, hamburger-toggled. R1.3-D9 still applies here.
  //
  // Pre-R7.12: pinned inline only at ≥1536px (3xl) and only when user
  // had opted-in. Sheet drawer at all other sizes. R7.12 lowers that
  // breakpoint to 1024px and makes pinning the default.
  const isWide = useMediaQuery("(min-width: 1024px)");
  const [sidebarPref, setSidebarPrefState] = React.useState<SidebarPref>(readSidebarPref);
  const [sidebarOpen, setSidebarOpen] = React.useState<boolean>(false);
  // The rail renders at ≥1024px in workspace mode. <1024px keeps the
  // Sheet drawer for the R6.6 mobile pattern.
  const railVisible = isWide && chromeWorkspace;

  // Close the (mobile) Sheet on every navigation. No equivalent for
  // the rail because it's persistent — its open/closed state is
  // expressed as mini vs expanded, not visibility.
  React.useEffect(() => {
    setSidebarOpen(false);
  }, [route, routeParam]);

  /** Toggle between mini and expanded — used by the rail's chevron. */
  const toggleRailMode = (): void => {
    const next: SidebarPref = {
      mode: sidebarPref.mode === "mini" ? "expanded" : "mini",
    };
    setSidebarPrefState(next);
    writeSidebarPref(next);
  };

  /** Open/close the <1024px Sheet drawer. The mobile path doesn't
   *  persist; users expect mobile drawers to default-close on every
   *  page entry. */
  const setSidebarOpenWithPersist = (next: boolean): void => {
    setSidebarOpen(next);
  };

  // R1.3 B1 — sticky-navbar scroll-aware shadow. Toggle data-scrolled
  // on <html> when scrollY > 4 so CSS can boost opacity + add depth
  // without making the nav opaque.
  React.useEffect(() => {
    const onScroll = (): void => {
      const scrolled = window.scrollY > 4;
      document.documentElement.dataset.scrolled = scrolled ? "true" : "false";
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Auth gate (workspace + not authed → /login) + landing redirect
  // (landing + authed → /dashboard). Both run via the same effect to
  // keep the holding-skeleton render logic centralised.
  React.useEffect(() => {
    if (isWorkspace && !auth.isAuthenticated) go("login");
    else if (redirectAuthedFromLanding) {
      // Role-aware landing: an authed user who hits the public landing goes to
      // THEIR home (role.homeRoute), not a hardcoded student /dashboard. Matches
      // the login flows (ROLES[role].homeRoute) + FRONTEND_MASTER_SPEC §Home.
      go(activeRoleId ? (ROLES[activeRoleId]?.homeRoute ?? "dashboard") : "dashboard");
    }
  }, [isWorkspace, auth.isAuthenticated, redirectAuthedFromLanding, activeRoleId, go]);

  if (isWorkspace && !auth.isAuthenticated) {
    return <AuthLoadingSkeleton label="در حال انتقال به ورود..." />;
  }
  if (redirectAuthedFromLanding) {
    return <AuthLoadingSkeleton label="در حال انتقال..." />;
  }

  // Authed marketplace browsing gets the WORKSPACE nav (hamburger → the mobile
  // sidebar drawer) rather than the bare public nav.
  const navMode = navModeFor(chromeWorkspace ? "WORKSPACE" : kind);

  return (
    <>
      <a href="#main-content" className="skip-link">
        پرش به محتوای اصلی
      </a>
      <ScrollProgress />
      {/* D49 (R-Landing-v2 polish round 2 ITEM 0) — owner-explicit
          minor AppShell exception under D11 urgency umbrella:
          on landing route `/`, AppShell skips its global Nav because
          Home.tsx renders its own `.home-nav-v2` inside `.home-shell-v2`
          (the Q2.b/Q3.c design implementation). Without this gate the
          two navs stack visibly. All non-landing routes keep AppShell
          Nav — workspace/auth/public/help routes UNCHANGED. */}
      {isLandingRoute ? null : (
        <Nav
          current={route}
          go={go}
          mode={navMode}
          onWorkspaceMenuClick={() => setSidebarOpenWithPersist(!sidebarOpen)}
          // R7.5 — plumb open state for the hamburger's aria-expanded.
          // At ≥1024px workspace, the hamburger is hidden via CSS
          // (R7.12), so this value only matters at <1024px.
          workspaceMenuOpen={sidebarOpen}
        />
      )}
      <ErrorBoundary key={route + ":" + (routeParam || "")}>
        <main
          id="main-content"
          className={cn(
            "page-shell appshell-main",
            "pb-[calc(64px+env(safe-area-inset-bottom))] md:pb-0",
          )}
          tabIndex={-1}
          data-route-kind={kind}
        >
          {/* Breadcrumb row — workspace routes (+ authed marketplace) only. */}
          {chromeWorkspace ? <Breadcrumbs /> : null}
          {chromeWorkspace ? (
            <div
              className={cn("workspace-grid", railVisible && "has-rail")}
              data-rail-mode={railVisible ? sidebarPref.mode : undefined}
              // R7.12 — Sidebar pinning indicator preserved for any
              // page-level CSS that still keys off it (5 legacy pages
              // per the workspace-grid comment); semantic for the new
              // pattern is data-rail-mode above.
              data-sidebar-pinned={railVisible ? "true" : "false"}
            >
              {/* R7.12 — Persistent rail at ≥1024px. Always in DOM
                  (not lazy-mounted), width animated via CSS variable
                  set on the grid wrapper above. */}
              {railVisible ? (
                <MiniRail
                  mode={sidebarPref.mode}
                  onToggle={toggleRailMode}
                  go={go}
                  active={pathKey}
                />
              ) : null}
              <div className="workspace-content">
                {retired ? (
                  <RetiredRoute routeId={pathKey} />
                ) : roleDenied ? (
                  <RoleDenied routeId={route} />
                ) : (
                  <Outlet />
                )}
              </div>
            </div>
          ) : (
            // شاخهٔ غیرکارتابلی هم باید گیت شود: «apply»/«admissions» عمومی‌اند
            // و هرگز به شاخهٔ بالا نمی‌رسند.
            <>{retired ? <RetiredRoute routeId={pathKey} /> : <Outlet />}</>
          )}
        </main>

        {/* R7.12 — Sidebar Sheet drawer at <1024px workspace mode ONLY.
            At ≥1024px the persistent rail above replaces it. Pre-R7.12
            the Sheet rendered at <1536px; R7.12 lowers the cutover to
            1024px so all desktop/tablet sizes get the inline rail. */}
        {chromeWorkspace && !railVisible ? (
          <Sheet open={sidebarOpen} onOpenChange={setSidebarOpenWithPersist}>
            <SheetContent
              side="start"
              id="appshell-sidebar-drawer"
              className="appshell-sidebar-drawer"
              aria-label="منوی workspace"
              onCloseAutoFocus={(e) => {
                e.preventDefault();
                document.getElementById("appshell-sidebar-trigger")?.focus();
              }}
            >
              <RoleSideNav active={pathKey} go={go} />
            </SheetContent>
          </Sheet>
        ) : null}

        {kind === "PUBLIC" && !chromeWorkspace && <Footer go={go} />}
        {/* Phase-A R1.3 Brand: workspace + auth_flow get a thin attribution
            bar (the marketing Footer above already carries the full version
            for PUBLIC). Owner asked for org credit on every page. The authed
            marketplace uses workspace chrome, so it gets the compact bar too. */}
        {kind !== "PUBLIC" || chromeWorkspace ? <OrgAttribution variant="compact" /> : null}
        {auth.isAuthenticated && chromeWorkspace ? <BottomNav /> : null}
      </ErrorBoundary>
    </>
  );
};

export default AppShell;
